'use client';

import type { ReactNode } from 'react';

/**
 * The control widths the prototype actually uses: a number-with-unit field
 * (~120px), a currency field (~150px), a short text field (~200px), a long one
 * (~320px) and the widest panel measure, the Skill Set tree's 536px value column.
 * DS `Input` is `w-full`, so a row control needs a bounded parent.
 */
type SettingsFieldWidth =
  | 'xs'
  | 'unit'
  | 'sm'
  | 'md'
  | 'select'
  | 'lg'
  | 'text'
  | 'nameFirst'
  | 'nameMiddle'
  | 'state'
  | 'zip'
  | 'radius'
  | 'card'
  | 'tree'
  | 'xl'
  | 'full';

type SettingsFieldProps = {
  width?: SettingsFieldWidth;
  children: ReactNode;
  className?: string;
};

const WIDTHS: Record<SettingsFieldWidth, string> = {
  xs: 'w-[7.5rem]',
  // The frames' number field: 140px, whether it carries a unit or a currency.
  unit: 'w-[8.75rem]',
  sm: 'w-[9.5rem]',
  md: 'w-[12.5rem]',
  // Wide enough for the longest option of a fixed-choice setting ("Leave it to a coordinator").
  select: 'w-[15rem]',
  lg: 'w-[20rem]',
  // The frames' single text input on the Users screens: 300px.
  text: 'w-[18.75rem]',
  // Full name row: first and last 150px, middle 130px. City uses `unit` (140px).
  nameFirst: 'w-[9.375rem]',
  nameMiddle: 'w-[8.125rem]',
  // Address row: State 110px, ZIP 100px.
  state: 'w-[6.875rem]',
  zip: 'w-[6.25rem]',
  // The radius picker beside an address search: 150px.
  radius: 'w-[9.375rem]',
  // The design's 1040 container split 440 label / 64 gutter / 536 control.
  // Role permission cards: 496px.
  card: 'w-[31rem]',
  // The Skill set tree and the block-out list: the 488px value column.
  tree: 'w-[30.5rem]',
  xl: 'w-[33.5rem]',
  full: 'w-full',
};

/**
 * Bounds a DS control to one of the prototype's field widths.
 *
 * These are the prototype's own inline `style="width:120px"` measurements; the
 * design system has no field-width token, so they live here as a named set
 * instead of being sprinkled across call sites.
 */
export const SettingsField = ({ width = 'md', children, className }: SettingsFieldProps) => (
  <div
    data-lula-ds=""
    className={`min-w-0 max-w-full ${WIDTHS[width]}${className ? ` ${className}` : ''}`}
  >
    {children}
  </div>
);

export default SettingsField;
